import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { GroceryItem, CATEGORY_METADATA, GroceryCategory, GroceryItemSource } from '../../types/groceryList';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { formatQuantity } from '../../utils/ingredientParser';
import { stapleRank } from '../../constants/staples';

interface AlreadyHaveSectionProps {
  items: GroceryItem[];
  onExcludeStaple?: (stapleKey: string, stapleLabel: string) => void;
  onUndoExcludeStaple?: (stapleKey: string) => void;
}

const AlreadyHaveSection: React.FC<AlreadyHaveSectionProps> = ({
  items,
  onExcludeStaple,
  onUndoExcludeStaple
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [undoState, setUndoState] = useState<{ stapleKey: string; stapleLabel: string } | null>(null);

  if (items.length === 0) return null;

  const inventoryItems = items.filter((item: any) => item.source === 'inventory');
  const manualItems = items.filter((item: any) => item.source === 'manual-review');

  // Staples render in STAPLE_GROUPS declaration order, not the order the
  // recipes happened to mention them. The banner quotes the first three of
  // this list as its examples, so an unsorted list makes that copy shuffle
  // whenever the week's recipes are reordered.
  const stapleItems = items
    .filter((item: any) => item.source === 'staple')
    .slice()
    .sort((a: any, b: any) => stapleRank(a.stapleKey) - stapleRank(b.stapleKey));

  return (
    <View style={styles.container}>
      {/* Collapsible Header */}
      <TouchableOpacity
        style={styles.header}
        onPress={() => setIsExpanded(!isExpanded)}
        activeOpacity={0.7}
      >
        <View style={styles.headerLeft}>
          <Feather
            name="check-circle"
            size={18}
            color={Paprika.success}
          />
          <Text style={styles.headerTitle}>
            {t('groceryList.alreadyHave')}
          </Text>
          <View style={styles.countBadge}>
            <Text style={styles.countBadgeText}>{items.length}</Text>
          </View>
        </View>
        <Feather
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={20}
          color={Paprika.textSecondary}
        />
      </TouchableOpacity>

      {/* Expanded Content */}
      {isExpanded && (
        <View style={styles.content}>
          {/* Kitchen Staples */}
          {stapleItems.length > 0 && (
            <View style={styles.sourceGroup}>
              <View style={styles.sourceHeader}>
                <Feather name="award" size={14} color={Paprika.textMuted} />
                <Text style={styles.sourceLabel}>{t('groceryList.kitchenStaples')}</Text>
              </View>
              {stapleItems.map((item: any) => (
                <View key={item.id} style={styles.itemRow}>
                  <Feather name="check" size={16} color={Paprika.success} />
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{item.name}</Text>
                    {item.recipeNames.length > 0 && (
                      <Text style={styles.recipeAttribution} numberOfLines={1}>
                        {item.recipeBreakdown && item.recipeBreakdown.length > 1
                          ? item.recipeBreakdown.map((b: any) => {
                              const displayAmt = formatQuantity(b.amount, b.unit);
                              return `${b.recipeName} (${displayAmt})`;
                            }).join(' · ')
                          : item.recipeNames.join(', ')}
                      </Text>
                    )}
                  </View>
                  {!!item.stapleKey && onExcludeStaple && (
                    <TouchableOpacity
                      onPress={() => {
                        const label = t(`groceryList.staples.${item.stapleKey}`);
                        onExcludeStaple(item.stapleKey, label);
                        setUndoState({ stapleKey: item.stapleKey, stapleLabel: label });
                        // Auto-dismiss undo after 5 seconds
                        setTimeout(() => setUndoState(null), 5000);
                      }}
                      style={styles.actionButton}
                    >
                      <Text style={styles.actionText}>
                        {t('groceryList.staples.markMissing')}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Inventory Items */}
          {inventoryItems.length > 0 && (
            <View style={styles.sourceGroup}>
              <View style={styles.sourceHeader}>
                <Feather name="package" size={14} color={Paprika.textMuted} />
                <Text style={styles.sourceLabel}>{t('groceryList.inYourInventory')}</Text>
              </View>
              {inventoryItems.map((item: GroceryItem) => (
                <View key={item.id} style={styles.itemRow}>
                  <Feather name="check" size={16} color={Paprika.success} />
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{item.name}</Text>
                    {item.quantityInInventory !== undefined && item.quantityInInventory > 0 && (
                      <Text style={styles.itemDetail}>
                        {item.totalNeeded
                          ? t('groceryList.inStockNeed', { count: item.quantityInInventory, needed: item.totalNeeded })
                          : t('groceryList.inStock', { count: item.quantityInInventory })}
                      </Text>
                    )}
                    {item.recipeNames.length > 0 && (
                      <Text style={styles.recipeAttribution} numberOfLines={1}>
                        {item.recipeBreakdown && item.recipeBreakdown.length > 1
                          ? item.recipeBreakdown.map((b: any) => {
                              const displayAmt = formatQuantity(b.amount, b.unit);
                              return `${b.recipeName} (${displayAmt})`;
                            }).join(' · ')
                          : item.recipeNames.join(', ')}
                      </Text>
                    )}
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Manual Review Items */}
          {manualItems.length > 0 && (
            <View style={styles.sourceGroup}>
              <View style={styles.sourceHeader}>
                <Feather name="user-check" size={14} color={Paprika.textMuted} />
                <Text style={styles.sourceLabel}>{t('groceryList.confirmedByYou')}</Text>
              </View>
              {manualItems.map((item: GroceryItem) => (
                <View key={item.id} style={styles.itemRow}>
                  <Feather name="check" size={16} color={Paprika.success} />
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{item.name}</Text>
                    {item.recipeNames.length > 0 && (
                      <Text style={styles.recipeAttribution} numberOfLines={1}>
                        {item.recipeBreakdown && item.recipeBreakdown.length > 1
                          ? item.recipeBreakdown.map((b: any) => {
                              const displayAmt = formatQuantity(b.amount, b.unit);
                              return `${b.recipeName} (${displayAmt})`;
                            }).join(' · ')
                          : item.recipeNames.join(', ')}
                      </Text>
                    )}
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Undo Toast */}
          {undoState && (
            <View style={styles.undoToast}>
              <Text style={styles.undoToastText}>
                {t('groceryList.staples.excludedToast', { name: undoState.stapleLabel })}
              </Text>
              {onUndoExcludeStaple && (
                <TouchableOpacity
                  onPress={() => {
                    onUndoExcludeStaple(undoState.stapleKey);
                    setUndoState(null);
                  }}
                >
                  <Text style={styles.undoButton}>{t('common.undo')}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: PaprikaSpacing.lg,
    backgroundColor: `${Paprika.success}08`,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: `${Paprika.success}20`,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: 14,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    fontSize: 15,
  },
  countBadge: {
    backgroundColor: `${Paprika.success}20`,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  countBadgeText: {
    ...PaprikaTypography.caption,
    color: Paprika.success,
    fontWeight: '600',
  },
  content: {
    paddingHorizontal: PaprikaSpacing.md,
    paddingBottom: PaprikaSpacing.md,
  },
  sourceGroup: {
    marginBottom: PaprikaSpacing.sm,
  },
  sourceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: `${Paprika.success}15`,
  },
  sourceLabel: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 6,
    gap: 10,
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
  itemDetail: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: 2,
  },
  recipeAttribution: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: 2,
    fontStyle: 'italic',
  },
  actionButton: {
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: 4,
  },
  actionText: {
    ...PaprikaTypography.caption,
    color: Paprika.primary,
    fontWeight: '600',
  },
  undoToast: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Paprika.textPrimary,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.sm,
    borderRadius: PaprikaRadius.md,
    marginTop: PaprikaSpacing.md,
  },
  undoToastText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.surface,
    flex: 1,
  },
  undoButton: {
    ...PaprikaTypography.caption,
    color: Paprika.primary,
    fontWeight: '600',
    marginLeft: PaprikaSpacing.md,
  },
});

export default AlreadyHaveSection;
